import { 
  FileCheck, 
  ShieldCheck, 
  AlertTriangle, 
  Lock, 
  CheckCircle2, 
  Calendar, 
  UserCheck, 
  Layers
} from 'lucide-react';

const GUIDELINES = [
  {
    number: '01',
    title: 'One Active Task per Engineer',
    category: 'Workload & SLA Invariant',
    description: 'A Detection Engineer may only be actively assigned to a single detection rule at any given time. This guarantees strict adherence to the 3 working days development SLA.',
    enforcement: 'Automated backend check aborts assignment if engineer has any rule in ENGINEER_ASSIGNED, IN_PROGRESS, TESTING, or READY_FOR_VALIDATION.',
    icon: UserCheck,
    color: 'blue'
  },
  {
    number: '02',
    title: 'Minimum Two Validations',
    category: 'Development Verification Gate',
    description: 'Before an engineer can pass development sign-off, exactly 2 independent test validations are mandatory: Test 1 (Simulated Attack / True Positive) and Test 2 (Benign Baseline / False Positive check).',
    enforcement: 'Backend blocks transition with a 400 error unless exactly two structured validation results are submitted and verified by Shift Leads.',
    icon: CheckCircle2,
    color: 'emerald'
  },
  {
    number: '03',
    title: 'Mandatory 5 UAT Artifacts',
    category: 'UAT Deployment Gate',
    description: 'Step 10 UAT deployment is locked until all 5 mandatory artifacts are recorded: (1) Alert Proof Screenshot, (2) Entity Mapping, (3) MITRE ATT&CK Mapping, (4) Rule Execution Schedule, and (5) Lookback Window Duration.',
    enforcement: 'Backend workflow transition rejects deployment requests missing any of the 5 required telemetry artifacts.',
    icon: Layers,
    color: 'purple'
  },
  {
    number: '04',
    title: 'Seven-Day UAT Review Soak Period',
    category: 'Noise & Fidelity Protocol',
    description: 'Rules deployed to UAT must undergo a mandatory 7-day soaking window under real-world telemetry before the Weekly Review meeting is convened.',
    enforcement: 'Automated SLA calculator schedules the Weekly Review exactly 7 calendar days after UAT deployment.',
    icon: Calendar,
    color: 'amber'
  },
  {
    number: '05',
    title: 'Final Approval Required (Aravind)',
    category: 'Dual Executive Sign-Off (Step 13)',
    description: 'Following the Post Meeting Report (PMR), Aravind Chakkaravarthy must review rule metrics and provide formal executive sign-off with mandatory review comments.',
    enforcement: 'Transition to Step 14 is impossible without valid Aravind Final Approval recorded in the database history.',
    icon: ShieldCheck,
    color: 'rose'
  },
  {
    number: '06',
    title: 'Production Approval Required (Logesh & Heman)',
    category: 'Dual Executive Sign-Off (Step 14)',
    description: 'Shift Leads Logesh & Heman conduct the final production readiness review and grant secondary deployment clearance.',
    enforcement: 'Backend enforces that Step 13 has succeeded prior to accepting Step 14 production approval.',
    icon: Lock,
    color: 'indigo'
  },
  {
    number: '07',
    title: 'Production Deployment Security Gate',
    category: 'Live Release Authorization',
    description: 'Step 15 live release to SIEM / Microsoft Sentinel production monitoring is strictly locked until both approvals (Step 13 and Step 14) are verified.',
    enforcement: 'Critical security gate in socWorkflowEngine.ts prohibits production deployment execution unless current status is PRODUCTION_APPROVED.',
    icon: AlertTriangle,
    color: 'emerald'
  }
];

export function UserGuidelinesView() {
  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-blue-500" />
          <span>SOC Governance Workflow Rules & Guidelines</span>
        </h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Standard Operating Procedures (SOP) and security gates enforced across all detection engineering use cases.
        </p>
      </div>

      {/* GUIDELINES LIST */}
      <div className="space-y-4">
        {GUIDELINES.map((rule) => {
          const Icon = rule.icon;
          return (
            <div
              key={rule.number}
              className="p-5 rounded-2xl border border-border bg-card shadow-xs flex flex-col md:flex-row md:items-start justify-between gap-5"
            >
              <div className="flex items-start gap-4 flex-1">
                <div className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-500 shrink-0">
                  <Icon className="h-5 w-5" />
                </div>

                <div className="space-y-1.5 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold text-foreground">{rule.title}</h3>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                      {rule.category}
                    </span>
                  </div>

                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {rule.description}
                  </p>

                  <div className="mt-2 p-2.5 rounded-lg bg-muted/40 border border-border/80 text-[11px] text-foreground flex items-start gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                    <span><strong>Enforcement:</strong> {rule.enforcement}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
